/**
 * TraceDetailPanel — single source-of-truth UI for "show one trace's
 * input / output / feedback / metadata".
 *
 * Replaces (in phases 5-8) the parallel UIs that historically rendered the
 * same info differently:
 *   - FeedbackTraceDetailDialog (modal — UserDrilldown Feedback tab)
 *   - FeedbackPage inline expansion
 *   - The shared <TraceTable expandable> per-row inline expansion (Phase 8)
 *
 * The component is presentation-only; the caller passes in `detail` already
 * fetched (e.g. via `useFeedbackTraceDetail`).
 *
 * Subsections rendered, in order:
 *   1. Header — timestamp / duration / cost / model / external Langfuse link
 *   2. Input  — collapsed-by-default markdown / JSON code-block (~10 lines)
 *   3. Output — same shape as Input
 *   4. Feedback (when `feedback` prop set) — rating badge + italic blockquote
 *   5. Metadata accordion — pretty-printed raw `metadata` JSONB
 *
 * Markdown rendering: `react-markdown` + `remark-gfm` render non-JSON
 * content. Callers passing Slack mrkdwn (e.g. brief viewer) must run
 * `slackMrkdwnToMarkdown` first — `extractDisplayText` does not detect
 * Slack-flavored input.
 */

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import { BriefMessageViewerByTrace } from '@/components/BriefMessageViewer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { langfuseTraceUrl } from '@/lib/langfuse';
import { slackMrkdwnToMarkdown } from '@/lib/slack-mrkdwn';
import { extractDisplayText } from '@/lib/trace-content';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * The trace detail object as returned by GET
 * /salescoach/api/feedback/trace-detail. The current backend (pre-Phase-2)
 * returns just `{input, output, error}`; richer fields below are optional so
 * a Phase 2 expansion (timestamp / duration / cost / model / metadata) drops
 * in without breaking callers.
 */
export interface TraceDetail {
  /** Trace identifier — used to build the Langfuse external link. */
  traceId?: string;
  /** ISO timestamp of the trace. */
  timestamp?: string | null;
  /** End-to-end duration in ms. */
  durationMs?: number | null;
  /** Total LLM cost in USD. */
  cost?: number | null;
  /** Model name (e.g. "gemini-3-flash-preview"). */
  model?: string | null;
  /** Untruncated input — raw JSONB or string. */
  input: unknown;
  /** Untruncated output — raw JSONB or string. */
  output: unknown;
  /** Server-side error string (e.g. 'trace_not_found'). */
  error?: string | null;
  /** Raw metadata JSONB — pretty-printed in the metadata accordion. */
  metadata?: unknown;
  /**
   * What the CODE drew beside the model's answer — the connect card today.
   *
   * A Slack reply is the coach's text PLUS blocks the product adds: the line naming
   * a credential the user is missing and the red button that fixes it. The turn
   * records them verbatim on its trace (`codeDrawnBlocks`), and they are shown as
   * their own section so a reader can tell the two halves apart. Absent / empty on
   * the great majority of turns, which drew nothing.
   */
  codeDrawn?: CodeDrawnBlock[] | null;
}

/** One block the code drew. Hand-synced with `CodeDrawnBlock` in
 *  `src/services/task-receipt.ts`; `kind` is widened to `string` because a turn
 *  recorded by a newer bot may name a kind this build has never heard of. */
export interface CodeDrawnBlock {
  kind: string;
  /** The literal text the user saw, in SLACK mrkdwn — converted before render. */
  text: string;
  /** The button's label, when the block carried one. Shown as an inert chip. */
  button?: string | null;
}

export interface TraceDetailPanelProps {
  /** The trace detail object as returned by the trace-detail endpoint. */
  detail: TraceDetail;
  /**
   * Optional rating + free-text feedback — when set, rendered in a
   * 'Feedback' subsection between Output and Metadata.
   */
  feedback?: { rating: number | null; text: string | null } | null;
  /**
   * Layout — `inline` drops the outer Card chrome (fits inside an expanded
   * row), `modal` wraps the panel in a Card so it stands alone inside a
   * dialog. Defaults to `inline` because the inline use is more common.
   */
  variant?: 'inline' | 'modal';
  /** Show the Langfuse external link in the header. Defaults to true. */
  showLangfuseLink?: boolean;
  /**
   * When true, the trace is a delivered brief — render the FULL Slack content
   * (fetched from `brief_messages` by trace id) in place of the bare status
   * string the trace stores as output ("sent N messages"). Callers compute
   * this from what they have: FeedbackTable from `row.isBrief`, TraceTable via
   * `isBriefTraceName(row.name)`. Defaults false (render raw output).
   */
  isBrief?: boolean;
}

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function formatDuration(ms: number | null | undefined): string | null {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatCost(usd: number | null | undefined): string | null {
  if (usd == null || usd === 0) return null;
  if (usd < 0.01) return `<$0.01`;
  return `$${usd.toFixed(3)}`;
}

function formatTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, 'Z');
}

function starString(rating: number): string {
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return '★'.repeat(filled) + '☆'.repeat(5 - filled);
}

/**
 * Collapsible content block — clamps to ~10 lines via `line-clamp-[10]` and
 * exposes a "Show all" / "Show less" toggle. When `isJson` is true the body
 * is rendered as a `<pre>` code block (mono font, horizontal scroll, no
 * markdown attempt).
 */
function ContentBlock({
  label,
  text,
  isJson,
  testIdPrefix,
  keepLineBreaks = false,
}: {
  label: string;
  text: string;
  isJson: boolean;
  testIdPrefix: string;
  /**
   * Render a single newline as a line break, the way Slack does.
   *
   * OFF by default, because Input and Output are rendered as markdown and have been
   * read that way since this panel shipped. ON for Slack-authored blocks, where a
   * newline is a line the user saw: the connect card stacks two credential sentences
   * in one section joined by `\n`, and CommonMark folds that into a space — one
   * run-on paragraph in the section whose claim is "what was on the screen". Same
   * plugin pair the brief viewer uses for the same reason.
   */
  keepLineBreaks?: boolean;
}): React.ReactElement {
  const [showAll, setShowAll] = React.useState(false);

  return (
    <section data-testid={`${testIdPrefix}-block`} className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>

      {isJson ? (
        <pre
          data-testid={`${testIdPrefix}-body`}
          className={cn(
            'rounded border border-border bg-background p-2 font-mono text-xs leading-snug overflow-x-auto whitespace-pre',
            showAll ? '' : 'max-h-[15rem] overflow-y-auto',
          )}
        >
          {text}
        </pre>
      ) : (
        <div
          data-testid={`${testIdPrefix}-body`}
          className={cn(
            'rounded border border-border bg-background p-2 text-sm leading-snug break-words',
            'prose prose-sm max-w-none dark:prose-invert',
            'prose-p:my-1 prose-headings:my-2 prose-pre:my-1 prose-li:my-0',
            'prose-a:text-primary prose-a:underline-offset-2',
            'prose-code:font-mono prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none',
            showAll ? '' : 'max-h-[15rem] overflow-y-auto',
          )}
        >
          <ReactMarkdown
            remarkPlugins={keepLineBreaks ? [remarkGfm, remarkBreaks] : [remarkGfm]}
            components={{
              a: ({ node: _node, ...props }) => (
                <a
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                />
              ),
            }}
          >
            {text}
          </ReactMarkdown>
        </div>
      )}

      <button
        type="button"
        onClick={(e) => {
          // Stop propagation so a row-level expand toggle (FeedbackTable
          // mobile, SortableTable mobile cards) doesn't collapse the row
          // when the user is just trying to expand the truncated content.
          e.stopPropagation();
          setShowAll((v) => !v);
        }}
        data-testid={`${testIdPrefix}-toggle`}
        // Mobile-first sizing: 11px target was below comfortable tap reach
        // on phones. Bump to a 28px-min control with explicit padding so
        // it reads as a control, not a passing link, on touch devices.
        // Desktop falls back to the previous compact size.
        className="inline-flex items-center min-h-7 -mx-1 rounded px-1 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:text-[11px]"
      >
        {showAll ? 'Show less' : 'Show all'}
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TraceDetailPanel
// ---------------------------------------------------------------------------

export function TraceDetailPanel({
  detail,
  feedback = null,
  variant = 'inline',
  showLangfuseLink = true,
  isBrief = false,
}: TraceDetailPanelProps): React.ReactElement {
  const inputDisplay = React.useMemo(
    () => extractDisplayText(detail.input, 'input'),
    [detail.input],
  );
  const outputDisplay = React.useMemo(
    () => extractDisplayText(detail.output, 'output'),
    [detail.output],
  );

  // What the code drew, and the labels of any buttons among those blocks. Defensive
  // about the entries themselves: they arrive from a trace written by whichever bot
  // build served that turn, so an entry with no text is dropped rather than rendered
  // as an empty panel.
  const codeDrawn = React.useMemo(
    () => (detail.codeDrawn ?? []).filter((b) => typeof b?.text === 'string' && b.text !== ''),
    [detail.codeDrawn],
  );
  const theButtonsDrawn = codeDrawn
    .map((b) => b.button)
    .filter((label): label is string => typeof label === 'string' && label !== '');

  const ts = formatTimestamp(detail.timestamp);
  const dur = formatDuration(detail.durationMs);
  const cost = formatCost(detail.cost);
  const traceUrl = detail.traceId ? langfuseTraceUrl(detail.traceId) : null;

  // Header meta line — drop empty entries so the row stays terse when only a
  // subset of fields is populated (the current API returns just input/output).
  const metaParts: React.ReactNode[] = [];
  if (ts) metaParts.push(<span key="ts">{ts}</span>);
  if (dur) metaParts.push(<span key="dur">{dur}</span>);
  if (cost) metaParts.push(<span key="cost">{cost}</span>);
  if (detail.model) {
    metaParts.push(
      <span key="model" className="font-mono">
        {detail.model}
      </span>,
    );
  }

  const hasMetadata =
    detail.metadata != null &&
    typeof detail.metadata === 'object' &&
    Object.keys(detail.metadata as Record<string, unknown>).length > 0;

  const body = (
    <div
      data-testid="trace-detail-panel"
      data-variant={variant}
      className="space-y-3 text-sm"
    >
      {/* 1. Header */}
      <div
        data-testid="trace-detail-panel-header"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
      >
        {metaParts.length > 0 ? (
          metaParts.map((part, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <span>·</span>}
              {part}
            </React.Fragment>
          ))
        ) : detail.traceId ? (
          <span className="font-mono">{detail.traceId}</span>
        ) : null}

        {showLangfuseLink && traceUrl && (
          <a
            href={traceUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trace-detail-panel-langfuse-link"
            // Mobile: nudge the link to its own row when the meta line
            // wraps so the tap-target is the full link width, not a
            // sliver hanging off the right edge of the previous chip.
            className="ml-auto inline-flex min-h-7 items-center rounded px-1 text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            onClick={(e) => e.stopPropagation()}
          >
            → open in Langfuse
          </a>
        )}
      </div>

      {/* trace_not_found / soft errors */}
      {detail.error === 'trace_not_found' && (
        <div
          data-testid="trace-detail-panel-not-found"
          className="italic text-muted-foreground"
        >
          Trace content is not available in Langfuse (it may have been purged
          or rotated out of retention).
        </div>
      )}
      {detail.error && detail.error !== 'trace_not_found' && (
        <div
          data-testid="trace-detail-panel-error"
          className="rounded border border-destructive/40 bg-background p-2 text-destructive"
        >
          {detail.error}
        </div>
      )}

      {/* 2. Input */}
      {inputDisplay.text && (
        <ContentBlock
          label="Input (request)"
          text={inputDisplay.text}
          isJson={inputDisplay.isJson}
          testIdPrefix="trace-detail-panel-input"
        />
      )}

      {/* 3. Output — a delivered brief renders its FULL Slack content (the same
           canonical viewer as the Briefs tab), fetched from brief_messages by
           trace id, instead of the bare status string ("sent N messages"). */}
      {isBrief && detail.traceId ? (
        <section data-testid="trace-detail-panel-brief" className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Delivered brief
          </div>
          <BriefMessageViewerByTrace traceId={detail.traceId} />
        </section>
      ) : (
        outputDisplay.text && (
          <ContentBlock
            label="Output (response)"
            text={outputDisplay.text}
            isJson={outputDisplay.isJson}
            testIdPrefix="trace-detail-panel-output"
          />
        )
      )}

      {/* 3b. What the CODE drew — the connect card and, later, its siblings.
           Its own section because it is NOT the coach speaking: the model is
           forbidden to mention the credential at all, and these blocks are the
           product's, drawn deterministically around the answer.

           The text is converted from Slack mrkdwn FIRST — this panel's markdown
           renderer knows nothing of Slack, so `*bold*` would render italic and
           `<URL|label>` as a literal. The button is a RECORD of a control on
           somebody else's screen, so it is an inert chip (the same one the brief
           viewer uses for the feedback prompt's buttons), never a real control. */}
      {codeDrawn.length > 0 && (
        <section
          data-testid="trace-detail-panel-code-drawn"
          aria-label="Added by the code"
          className="space-y-1"
        >
          <ContentBlock
            label="Added by the code (not Mr. Edge)"
            text={codeDrawn.map((b) => slackMrkdwnToMarkdown(b.text)).join('\n\n')}
            isJson={false}
            testIdPrefix="trace-detail-panel-code-drawn"
            keepLineBreaks
          />
          {theButtonsDrawn.length > 0 && (
            <div
              data-testid="trace-detail-panel-code-drawn-actions"
              className="flex flex-wrap gap-1.5"
            >
              {theButtonsDrawn.map((label, idx) => (
                <span
                  key={`${label}-${idx}`}
                  className="inline-flex items-center rounded border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground"
                  aria-disabled="true"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      {/* 4. Feedback (optional) */}
      {feedback &&
        (feedback.rating !== null ||
          (feedback.text && feedback.text.trim() !== '')) && (
          <section
            data-testid="trace-detail-panel-feedback"
            className="space-y-1"
          >
            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Feedback
            </div>
            <div className="rounded border border-border bg-background p-2 text-sm space-y-1">
              {feedback.rating !== null && (
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="font-mono text-amber-600"
                    aria-label={`rating ${feedback.rating}`}
                  >
                    {starString(feedback.rating)}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {feedback.rating}/5
                  </span>
                </div>
              )}
              {feedback.text && feedback.text.trim() !== '' && (
                <blockquote
                  data-testid="trace-detail-panel-feedback-text"
                  className="italic whitespace-pre-wrap border-l-2 border-border pl-2 text-muted-foreground"
                >
                  &ldquo;{feedback.text}&rdquo;
                </blockquote>
              )}
            </div>
          </section>
        )}

      {/* 5. Metadata accordion */}
      {hasMetadata && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              data-testid="trace-detail-panel-metadata-toggle"
              className="text-xs"
            >
              Metadata
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre
              data-testid="trace-detail-panel-metadata-body"
              className="mt-2 rounded border border-border bg-background p-2 font-mono text-xs leading-snug overflow-x-auto whitespace-pre"
            >
              {JSON.stringify(detail.metadata, null, 2)}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );

  if (variant === 'modal') {
    return (
      <Card data-testid="trace-detail-panel-card">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Trace detail
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">{body}</CardContent>
      </Card>
    );
  }

  return body;
}
